import React, { useState, forwardRef } from 'react'
import { Select, formatSelectOptions } from '../../../components'
import { MILESTONE_TYPES, isInterviewMilestone } from '../helpers/milestones'
import { startTimeOptions, endTimeOptions } from '../helpers/timeOptions'

const DEFAULT_START_TIME = '10:00'

// Split a stored slot ("2026-08-05T10:00:00" or a Date-ish string) into the date and time
// halves the two inputs bind to. Deliberately string-sliced rather than parsed through
// Date: the column is a timestamp *without* time zone, i.e. already wall-clock time, and
// round-tripping it through Date would re-interpret it as local and shift it.
const splitSlot = (value) => {
  if (!value) return { date: '', time: '' }
  const s = String(value)
  return { date: s.slice(0, 10), time: s.slice(11, 16) }
}

const _CandidateMilestoneForm = ({ mode, data, submitForm, loading, setLoading }, ref) => {

  const record = data && data[0]
  const isBulk = mode !== 'edit' && data && data.length > 1

  // In edit mode `record` is the milestone row; the interview it booked (if any) is passed
  // alongside as `record.interview` by whoever opened the dialog.
  const existingSlot = splitSlot(mode === 'edit' ? record?.interview?.interview_start_timestamp : null)
  const existingEnd  = splitSlot(mode === 'edit' ? record?.interview?.interview_end_timestamp : null)

  // "edit" pre-fills from the milestone entry being edited; "add" for a single
  // candidate pre-fills the type from their current stage; "add" for multiple
  // candidates leaves it blank since they may not share a stage.
  const [formData, setFormData] = useState(mode === 'edit'
    ? {
        milestone_type: record?.milestone_type || '',
        milestone_date: record?.milestone_date ? record.milestone_date.slice(0, 10) : '',
        milestone_note: record?.milestone_note || '',
        interview_date:     existingSlot.date,
        interview_time:     existingSlot.time || DEFAULT_START_TIME,
        interview_end_time: existingEnd.time,
        interview_location: record?.interview?.interview_location || '',
      }
    : {
        milestone_type: isBulk ? '' : (record?.current_milestone_type || ''),
        milestone_date: '',
        milestone_note: '',
        interview_date:     '',
        interview_time:     DEFAULT_START_TIME,
        interview_end_time: '',
        interview_location: '',
      })
  const [invalid, setInvalid] = useState({})

  const isInterview = isInterviewMilestone(formData.milestone_type)

  const handleInputChange = (event) => {
    let { name, value } = ''

    if (event.target !== undefined) {
      name = event.target.name
      value = event.target.value
    } else {
      name = event.name
      value = event.value
    }

    const invalidForm = { ...invalid }
    delete invalidForm[name]
    setInvalid(invalidForm)

    const next = { ...formData, [name]: value }
    // Moving the start past the chosen end has to drop the end, or the form would keep a
    // value the Till picker no longer offers (and the API would reject it).
    if (name === 'interview_time' && next.interview_end_time && next.interview_end_time <= value) {
      next.interview_end_time = ''
    }
    setFormData(next)
  }

  const onSubmit = (event) => {
    event.preventDefault()

    if (!formData.milestone_type) {
      setInvalid({ milestone_type: { message: 'Du måste välja ett skede' } })
      return
    }

    // An interview stage without a booked slot is the state this form exists to prevent.
    // The API enforces the same rule (the board can post a stage change directly), so
    // this check is the friendly half, not the only one.
    if (isInterview && !formData.interview_date) {
      setInvalid({ interview_date: { message: 'Välj ett datum för intervjun' } })
      return
    }
    if (isInterview && !formData.interview_time) {
      setInvalid({ interview_time: { message: 'Välj en starttid för intervjun' } })
      return
    }

    setLoading(true)
    submitForm({
      milestone_type: formData.milestone_type,
      // An interview's milestone_date is deliberately left to the server's CURRENT_DATE
      // (the date the booking was made). The slot itself travels in `interview` — writing
      // a future date into milestone_date would make a booking outrank later milestones,
      // including "Anställd". See the column's COMMENT in the DB.
      milestone_date: isInterview ? undefined : (formData.milestone_date || undefined),
      milestone_note: formData.milestone_note.trim() || undefined,
      ...(isInterview ? {
        interview: {
          start_timestamp: `${formData.interview_date}T${formData.interview_time}:00`,
          end_timestamp:   formData.interview_end_time ? `${formData.interview_date}T${formData.interview_end_time}:00` : undefined,
          location:        formData.interview_location.trim() || undefined,
        },
      } : {}),
    })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>

        {isBulk &&
          <p style={{ color: 'var(--color-dark-grey)', marginBottom: 16 }}>Gäller {data.length} kandidater.</p>
        }

        <div className="form-block vertical">
          <label>Skede</label>
          <Select
            name="milestone_type"
            value={formData.milestone_type}
            options={formatSelectOptions({ options: MILESTONE_TYPES, optionValue: 'value', optionLabel: 'label' })}
            onChange={handleInputChange}
            disabled={loading}
            invalid={invalid.milestone_type}
            placeholder="Välj skede…"
          />
        </div>

        {isInterview ? (
          <>
            <p className="cd-form-hint">
              Intervjun bokas in och hamnar i kalendern på Hem, där kollegor kan anmäla sig.
              {isBulk && ' Alla valda kandidater får samma tid.'}
            </p>

            {/* Date on its own row (a native date input carries its own picker button and
                reads badly stretched across the modal), then the times as ordinary
                <Select>s — same control as Skede above, rather than each browser's own
                clock chrome. Till only offers times after Från. */}
            <div className="form-block vertical">
              <label>Datum<span className="label-required">*</span></label>
              <input
                className="cd-plain-input cd-slot-date"
                type="date"
                name="interview_date"
                value={formData.interview_date}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            {/* The two Selects sit DIRECTLY in .form-row and carry their own `label` prop —
                the idiom every other .form-row in the repo uses (PersonEditForm,
                BulkPersonImport). Wrapping them in `.form-block vertical` collapses both
                columns to zero width: that class sets `flex-grow: 0` at higher specificity
                than `.form-row > * { flex: 1 }`, so the children never grow and the two
                labels end up drawn on top of each other. */}
            <div className="form-row">
              <Select
                name="interview_time"
                label={<>Från <span className="label-required">*</span></>}
                value={formData.interview_time}
                options={startTimeOptions(formData.interview_time)}
                onChange={handleInputChange}
                disabled={loading}
                invalid={invalid.interview_time}
                placeholder="Välj tid…"
              />
              <Select
                name="interview_end_time"
                label={<>Till <span className="cd-slot-hint">valfritt</span></>}
                value={formData.interview_end_time}
                options={endTimeOptions(formData.interview_time, formData.interview_end_time)}
                onChange={handleInputChange}
                disabled={loading}
                isNullable
                placeholder="—"
              />
            </div>

            <div className="form-block vertical">
              <label>Plats</label>
              <input
                className="cd-plain-input"
                type="text"
                name="interview_location"
                value={formData.interview_location}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="Rum, adress eller möteslänk"
              />
            </div>
          </>
        ) : (
          <div className="form-block vertical">
            <label>Datum</label>
            <input
              className="cd-plain-input"
              type="date"
              name="milestone_date"
              value={formData.milestone_date}
              onChange={handleInputChange}
              disabled={loading}
            />
          </div>
        )}

        <div className="form-block vertical">
          <label>Anteckning</label>
          <textarea
            className="cd-plain-textarea"
            name="milestone_note"
            rows={3}
            value={formData.milestone_note}
            onChange={handleInputChange}
            disabled={loading}
          />
        </div>

        {Object.keys(invalid).length > 0 &&
          <div className="form-block form-error">
            {Object.entries(invalid).map(([key, value]) => (<p key={key}>{value.message}</p>))}
          </div>
        }
      </form>
    </div>
  )
}

export const CandidateMilestoneForm = forwardRef(_CandidateMilestoneForm)
